/**
 * Performance utilities for image and resource optimization
 */

const PRELOAD_BUDGET_WINDOW_MS = 60 * 1000
let preloadWindowStart = Date.now()
let preloadedCountInWindow = 0

/**
 * Preload images in the background to improve perceived performance
 * @param {string[]} imageUrls - Array of image URLs to preload
 * @returns {Promise<void>} - Resolves when all images are loaded or failed
 */
export const preloadImages = (imageUrls) => {
  return Promise.allSettled(
    imageUrls.map(
      (url) =>
        new Promise((resolve, reject) => {
          if (!url || typeof url !== 'string') {
            resolve()
            return
          }

          const img = new Image()
          img.onload = () => resolve()
          img.onerror = () => reject(new Error(`Failed to load: ${url}`))
          img.src = url
        })
    )
  )
}

/**
 * Create a tiny blur placeholder from a full image URL
 * Uses a tiny version loaded with aggressive compression
 * @param {string} imageUrl - Original image URL
 * @param {number} width - Tiny width (default: 10px)
 * @returns {string} - Modified URL or original if cannot modify
 */
export const getTinyBlurPlaceholder = (imageUrl, width = 10) => {
  if (!imageUrl || typeof imageUrl !== 'string') return ''

  try {
    const url = new URL(imageUrl)
    // For CDN images, you might have query params to resize
    // Adjust this based on your CDN capabilities
    url.searchParams.set('w', String(width))
    url.searchParams.set('q', '20') // Low quality for blur
    return url.toString()
  } catch {
    return imageUrl
  }
}

/**
 * Request an appropriately sized avatar/icon from CDN at twice the display size for retina/high-DPI screens.
 * Prevents downloading 1024px, 0.9MB raw originals for small 32px-76px avatar containers.
 * @param {string} imageUrl - Original avatar image URL
 * @param {number} targetDisplaySize - Display container size in px (e.g. 32 on mobile, 40 on desktop)
 * @param {number} devicePixelRatio - Device pixel ratio multiplier (default: 2)
 * @returns {string} - Optimized URL requesting 2x dimensions from CDN
 */
export const getOptimizedAvatarUrl = (imageUrl, targetDisplaySize = 40, devicePixelRatio = 2) => {
  if (!imageUrl || typeof imageUrl !== 'string') return imageUrl
  // Skip data URIs, blob URLs, and vector SVGs
  if (imageUrl.startsWith('data:') || imageUrl.startsWith('blob:') || imageUrl.endsWith('.svg')) {
    return imageUrl
  }

  try {
    const parsed = new URL(imageUrl, typeof window !== 'undefined' ? window.location.origin : 'https://www.erosuniverse.com')
    const targetPx = Math.round(targetDisplaySize * devicePixelRatio)

    if (parsed.searchParams.has('w') || parsed.searchParams.has('width')) {
      const existingW = Number(parsed.searchParams.get('w') || parsed.searchParams.get('width'))
      if (!existingW || existingW > targetPx) {
        if (parsed.searchParams.has('w')) parsed.searchParams.set('w', String(targetPx))
        if (parsed.searchParams.has('width')) parsed.searchParams.set('width', String(targetPx))
      }
    } else if (
      parsed.hostname.includes('erosuniverse.com') ||
      parsed.hostname.includes('cloudfront.net') ||
      parsed.hostname.includes('s3.') ||
      parsed.hostname.includes('amazonaws.com') ||
      parsed.hostname.includes('media') ||
      parsed.hostname.includes('cdn')
    ) {
      parsed.searchParams.set('w', String(targetPx))
      parsed.searchParams.set('h', String(targetPx))
      parsed.searchParams.set('fit', 'crop')
      parsed.searchParams.set('auto', 'format')
    }

    return parsed.toString()
  } catch {
    return imageUrl
  }
}

/**
 * Batch load images with priority queue
 * Loads high-priority images first, then low-priority ones
 * @param {Object[]} imageList - Array of {url, priority} objects
 * @param {number} concurrency - Max concurrent image loads (default: 3)
 * @returns {Promise<void>}
 */
export const batchLoadImages = async (imageList, concurrency = 3) => {
  if (!Array.isArray(imageList) || imageList.length === 0) return

  const budget = getAdaptivePreloadBudget()
  const now = Date.now()
  if (now - preloadWindowStart > PRELOAD_BUDGET_WINDOW_MS) {
    preloadWindowStart = now
    preloadedCountInWindow = 0
  }

  const remainingBudget = Math.max(0, budget.maxUrlsPerMinute - preloadedCountInWindow)
  if (remainingBudget === 0) return

  // Sort by priority (higher priority first)
  const sorted = [...imageList]
    .sort((a, b) => (b.priority || 0) - (a.priority || 0))
    .slice(0, remainingBudget)

  // Load in batches
  const safeConcurrency = Math.max(1, Math.min(concurrency, budget.maxConcurrency))
  for (let i = 0; i < sorted.length; i += safeConcurrency) {
    const batch = sorted.slice(i, i + safeConcurrency)
    await preloadImages(batch.map((item) => item.url))
    preloadedCountInWindow += batch.length

    if (preloadedCountInWindow >= budget.maxUrlsPerMinute) {
      break
    }
  }
}

/**
 * Global budget for background preload operations.
 * Protects long sessions from cumulative bandwidth usage.
 *
 * @returns {{maxUrlsPerMinute:number,maxConcurrency:number}}
 */
export const getAdaptivePreloadBudget = () => {
  if (typeof window === 'undefined') {
    return { maxUrlsPerMinute: 24, maxConcurrency: 2 }
  }

  const connection = navigator?.connection
  const effectiveType = connection?.effectiveType || ''
  const saveData = Boolean(connection?.saveData)
  const downlink = connection?.downlink || 10
  const memory = navigator?.deviceMemory || 4
  const cpuCores = navigator?.hardwareConcurrency || 4

  const isSlow =
    saveData ||
    effectiveType === 'slow-2g' ||
    effectiveType === '2g' ||
    downlink < 1.5 ||
    memory <= 2 ||
    cpuCores <= 4

  if (isSlow) {
    return { maxUrlsPerMinute: 8, maxConcurrency: 1 }
  }

  if (effectiveType === '3g' || downlink < 4) {
    return { maxUrlsPerMinute: 16, maxConcurrency: 2 }
  }

  return { maxUrlsPerMinute: 32, maxConcurrency: 3 }
}

/**
 * Returns adaptive preloading values based on current device/network capability.
 * Use this to reduce overfetching on slow networks and low-end devices.
 *
 * @param {Object} options
 * @param {'feed'|'gallery'|'template'} options.pageType
 * @returns {{triggerEvery:number,minItems:number,prefetchCount:number,concurrency:number}}
 */
export const getAdaptivePreloadConfig = ({ pageType = 'gallery' } = {}) => {
  if (typeof window === 'undefined') {
    return {
      triggerEvery: 12,
      minItems: 12,
      prefetchCount: 6,
      concurrency: 2
    }
  }

  const connection = navigator?.connection
  const effectiveType = connection?.effectiveType || ''
  const saveData = Boolean(connection?.saveData)
  const downlink = connection?.downlink || 10
  const memory = navigator?.deviceMemory || 4
  const cpuCores = navigator?.hardwareConcurrency || 4

  const isSlowNetwork =
    saveData ||
    effectiveType === 'slow-2g' ||
    effectiveType === '2g' ||
    downlink < 1.5
  const isMediumNetwork = effectiveType === '3g'
  const isLowMemory = memory <= 2
  const isLowCpu = cpuCores <= 4

  const baseByPage = {
    feed: { triggerEvery: 10, minItems: 10, prefetchCount: 5, concurrency: 2 },
    gallery: { triggerEvery: 12, minItems: 12, prefetchCount: 6, concurrency: 2 },
    template: { triggerEvery: 20, minItems: 20, prefetchCount: 10, concurrency: 3 }
  }

  const base = baseByPage[pageType] || baseByPage.gallery

  if (isSlowNetwork || isLowMemory || isLowCpu) {
    return {
      triggerEvery: Math.ceil(base.triggerEvery * 1.5),
      minItems: Math.ceil(base.minItems * 1.5),
      prefetchCount: Math.max(2, Math.floor(base.prefetchCount * 0.5)),
      concurrency: 1
    }
  }

  if (isMediumNetwork) {
    return {
      triggerEvery: Math.ceil(base.triggerEvery * 1.2),
      minItems: Math.ceil(base.minItems * 1.2),
      prefetchCount: Math.max(3, Math.floor(base.prefetchCount * 0.75)),
      concurrency: Math.max(1, base.concurrency - 1)
    }
  }

  return base
}

/**
 * Shared trigger decision for infinite-scroll preloading.
 * First preload happens at `minItems`, then every `triggerEvery` items.
 *
 * @param {number} totalItems
 * @param {{triggerEvery:number,minItems:number}} config
 * @returns {boolean}
 */
export const shouldRunAdaptivePreload = (totalItems, config) => {
  if (!Number.isFinite(totalItems) || totalItems <= 0) return false
  if (!config?.minItems || !config?.triggerEvery) return false
  if (totalItems < config.minItems) return false

  const delta = totalItems - config.minItems
  return delta % config.triggerEvery === 0
}

/**
 * Generate a base64 blur placeholder from canvas
 * Useful for progressive image loading
 * @param {string} imageUrl - Original image URL
 * @param {number} width - Placeholder width
 * @param {number} height - Placeholder height
 * @returns {Promise<string>} - Base64 data URL
 */
export const generateBlurPlaceholder = (imageUrl, width = 10, height = 10) => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)
        resolve(canvas.toDataURL('image/jpeg', 0.5))
      } catch (err) {
        reject(err)
      }
    }

    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = imageUrl
  })
}

/**
 * Intersection Observer based resource preloader
 * Preloads resources when they're about to enter viewport
 * @param {Element} element - DOM element to observe
 * @param {Function} onIntersect - Callback when element intersects
 * @param {Object} options - IntersectionObserver options
 * @returns {IntersectionObserver} - Observer instance
 */
export const observeForPreload = (element, onIntersect, options = {}) => {
  const defaultOptions = {
    rootMargin: '200px', // Start loading 200px before visible
    threshold: 0.01,
    ...options
  }

  const observer = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting) {
      onIntersect()
      observer.disconnect()
    }
  }, defaultOptions)

  observer.observe(element)
  return observer
}

/**
 * Cache for memoizing expensive computations
 * LRU cache with size limit
 */
export class LRUCache {
  constructor(maxSize = 100) {
    this.maxSize = maxSize
    this.cache = new Map()
  }

  get(key) {
    if (!this.cache.has(key)) return undefined
    const value = this.cache.get(key)
    // Refresh key (move to end)
    this.cache.delete(key)
    this.cache.set(key, value)
    return value
  }

  set(key, value) {
    // Delete if exists (to refresh position)
    if (this.cache.has(key)) {
      this.cache.delete(key)
    }
    // Remove oldest if at capacity
    else if (this.cache.size >= this.maxSize) {
      const oldestKey = this.cache.keys().next().value
      this.cache.delete(oldestKey)
    }
    this.cache.set(key, value)
  }

  clear() {
    this.cache.clear()
  }
}
